/**
 * @fileoverview Página responsável por acessar e organizar os fluxos de automação.
 */

import React, { useState, useEffect, useCallback, useContext } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, IconButton, Tooltip, Switch,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import {
  Plus, Workflow, MoreVertical, Trash2, Copy,
  Clock, Zap, ChevronRight, Search, Power, Database, ListChecks, Pencil, Bot,
} from "lucide-react";

import MainContainer from "../../components/MainContainer";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { toast } from "react-toastify";
import ContactBasesModal from "./components/ContactBasesModal";
import TriageManager from "./components/TriageManager";
import AgentManager from "./components/AgentManager";
import useTriageEnabled from "../../hooks/useTriageEnabled";
import usePermission, { isAdminUser } from "../../hooks/usePermission";
import { i18n } from "../../translate/i18n";

const PALETTE = [
  "#5B7CFF", "#7C5CFF", "#FF5C8A", "#FF8A5C",
  "#5CDFAA", "#FFD15C", "#5CC8FF", "#FF5C5C",
  "#A85CFF", "#5CFF8A", "#FF5CE0", "#8AFF5C",
];

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  const accent = "#5B7CFF";
  const textPrimary = isDark ? "#f0f2f5" : "#111318";
  const textSecondary = isDark ? "#a0aec0" : "#5a6478";
  const textFaint = isDark ? "#5a6478" : "#a0aec0";
  const border = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.07)";
  const surfaceCard = isDark
    ? "rgba(17, 19, 24, 0.85)"
    : "rgba(255, 255, 255, 0.9)";
  const surfaceHover = isDark
    ? "rgba(91, 124, 255, 0.06)"
    : "rgba(91, 124, 255, 0.03)";
  const bgPage = isDark ? "#0A0A0A" : "#f7f8fc";

  return {
    /* ── page ─────────────────────────────────────────────── */
    page: {
      padding: "28px 32px 40px",
      minHeight: "100%",
      fontFamily: "'Montserrat', sans-serif",
      "& *": {
        fontFamily: "'Montserrat', sans-serif",
      },
    },

    /* ── header ───────────────────────────────────────────── */
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 28,
      gap: 16,
    },
    headerLeft: {
      display: "flex",
      flexDirection: "column",
      gap: 2,
    },
    title: {
      fontSize: 24,
      fontWeight: 800,
      color: textPrimary,
      letterSpacing: "-0.02em",
      lineHeight: 1.2,
    },
    subtitle: {
      fontSize: 13,
      color: textSecondary,
      fontWeight: 500,
    },
    newBtn: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "10px 20px",
      borderRadius: 12,
      border: "none",
      background: accent,
      color: "#fff",
      fontSize: 13,
      fontWeight: 700,
      cursor: "pointer",
      transition: "all 0.2s ease",
      boxShadow: `0 2px 12px ${accent}44`,
      "&:hover": {
        background: "#4a6ae8",
        transform: "translateY(-1px)",
        boxShadow: `0 4px 20px ${accent}55`,
      },
      "&:active": {
        transform: "translateY(0)",
      },
    },

    // Botões no mesmo estilo da página /contacts (actionBtnBase/Primary/Secondary).
    actionBtnBase: {
      whiteSpace: "nowrap",
      flexShrink: 0,
      height: 36,
      minWidth: 0,
      padding: "0 14px",
      borderRadius: 10,
      fontSize: "0.875rem",
      fontWeight: 500,
      textTransform: "none",
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      cursor: "pointer",
      border: "none",
      fontFamily: "inherit",
      transition: "all 180ms cubic-bezier(0.4, 0, 0.2, 1)",
      "& svg": { flexShrink: 0 },
    },
    actionBtnPrimary: {
      background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
      color: "#ffffff",
      "&:hover:not(:disabled)": {
        background: "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)",
      },
    },
    actionBtnSecondary: {
      background: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.04)" : "rgba(15, 23, 42, 0.03)",
      color: theme.palette.type === "dark" ? "#f8fafc" : "#111827",
      border: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid rgba(15, 23, 42, 0.12)",
      "&:hover:not(:disabled)": {
        background: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(15, 23, 42, 0.06)",
        borderColor: theme.palette.type === "dark" ? "rgba(91, 124, 255, 0.4)" : "rgba(59, 130, 246, 0.35)",
        color: theme.palette.type === "dark" ? "#ffffff" : "#0f172a",
      },
    },

    /* ── search ───────────────────────────────────────────── */
    searchWrap: {
      position: "relative",
      marginBottom: 20,
      maxWidth: 360,
    },
    searchIcon: {
      position: "absolute",
      left: 12,
      top: "50%",
      transform: "translateY(-50%)",
      color: textFaint,
      pointerEvents: "none",
    },
    searchInput: {
      width: "100%",
      padding: "10px 14px 10px 38px",
      borderRadius: 10,
      border: `1px solid ${border}`,
      background: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
      color: textPrimary,
      fontSize: 13,
      outline: "none",
      transition: "border-color 0.2s",
      "&:focus": {
        borderColor: accent,
      },
      "&::placeholder": {
        color: textFaint,
      },
    },

    /* ── grid ─────────────────────────────────────────────── */
    grid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
      gap: 16,
    },

    /* ── card ─────────────────────────────────────────────── */
    card: {
      position: "relative",
      background: surfaceCard,
      border: `1px solid ${border}`,
      borderRadius: 16,
      padding: 0,
      overflow: "hidden",
      cursor: "pointer",
      transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
      "&:hover": {
        borderColor: isDark ? "rgba(91, 124, 255, 0.25)" : "rgba(91, 124, 255, 0.2)",
        background: surfaceHover,
        transform: "translateY(-2px)",
        boxShadow: isDark
          ? "0 8px 32px rgba(91, 124, 255, 0.12)"
          : "0 8px 32px rgba(0, 0, 0, 0.08)",
        "& $cardArrow": {
          opacity: 1,
          transform: "translateX(0)",
        },
      },
    },
    cardAccent: {
      height: 3,
      width: "100%",
    },
    cardBody: {
      padding: "16px 18px 14px",
    },
    cardTop: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      marginBottom: 10,
    },
    cardIcon: {
      width: 38,
      height: 38,
      borderRadius: 10,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },
    cardMenu: {
      padding: 4,
      marginTop: -2,
      marginRight: -4,
      color: textFaint,
      "&:hover": {
        color: textPrimary,
      },
    },
    cardTitle: {
      fontSize: 15,
      fontWeight: 700,
      color: textPrimary,
      marginBottom: 4,
      lineHeight: 1.3,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    cardMeta: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      fontSize: 11,
      color: textFaint,
      fontWeight: 500,
    },
    cardMetaItem: {
      display: "flex",
      alignItems: "center",
      gap: 4,
    },
    cardFooter: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginTop: 12,
      paddingTop: 10,
      borderTop: `1px solid ${border}`,
    },
    cardStatus: {
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      fontSize: 11,
      fontWeight: 600,
      padding: "3px 8px",
      borderRadius: 6,
    },
    statusActive: {
      background: isDark ? "rgba(34, 197, 94, 0.12)" : "rgba(34, 197, 94, 0.08)",
      color: "#22c55e",
    },
    statusInactive: {
      background: isDark ? "rgba(100, 116, 139, 0.12)" : "rgba(100, 116, 139, 0.08)",
      color: textFaint,
    },
    cardArrow: {
      opacity: 0,
      transform: "translateX(-4px)",
      transition: "all 0.2s",
      color: accent,
    },

    /* ── empty ────────────────────────────────────────────── */
    empty: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "80px 20px",
      gap: 16,
    },
    emptyIcon: {
      width: 64,
      height: 64,
      borderRadius: 20,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: isDark ? "rgba(91, 124, 255, 0.08)" : "rgba(91, 124, 255, 0.05)",
    },
    emptyTitle: {
      fontSize: 17,
      fontWeight: 700,
      color: textPrimary,
    },
    emptyText: {
      fontSize: 13,
      color: textSecondary,
      textAlign: "center",
      maxWidth: 340,
      lineHeight: 1.5,
    },

    /* ── dialog ───────────────────────────────────────────── */
    dialog: {
      "& .MuiDialog-paper": {
        borderRadius: 16,
        background: isDark ? "#14161e" : "#fff",
        border: `1px solid ${border}`,
        minWidth: 400,
        padding: 0,
      },
    },
    dialogTitle: {
      fontSize: 18,
      fontWeight: 800,
      color: textPrimary,
      padding: "20px 24px 4px",
    },
    dialogContent: {
      padding: "12px 24px 20px",
    },
    fieldLabel: {
      fontSize: 12,
      fontWeight: 600,
      color: textSecondary,
      marginBottom: 6,
      display: "block",
    },
    colorGrid: {
      display: "flex",
      flexWrap: "wrap",
      gap: 8,
      marginTop: 4,
    },
    colorDot: {
      width: 28,
      height: 28,
      borderRadius: 8,
      cursor: "pointer",
      transition: "all 0.15s",
      border: "2px solid transparent",
      "&:hover": {
        transform: "scale(1.15)",
      },
    },
    colorDotSelected: {
      border: `2px solid ${textPrimary}`,
      boxShadow: "0 0 0 2px rgba(91, 124, 255, 0.3)",
    },
    dialogActions: {
      padding: "8px 24px 20px",
      gap: 8,
    },
    cancelBtn: {
      color: textSecondary,
      fontWeight: 500,
      fontSize: 13,
      textTransform: "none",
      borderRadius: 10,
    },
    createBtn: {
      background: accent,
      color: "#fff",
      fontWeight: 500,
      fontSize: 13,
      textTransform: "none",
      borderRadius: 10,
      padding: "8px 24px",
      boxShadow: "none",
      "&:hover": {
        background: "#4a6ae8",
        boxShadow: "none",
      },
    },
    textField: {
      "& .MuiOutlinedInput-root": {
        borderRadius: 10,
        fontSize: 14,
        background: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
        "& fieldset": { borderColor: border },
        "&:hover fieldset": { borderColor: isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)" },
        "&.Mui-focused fieldset": { borderColor: accent },
      },
      "& .MuiOutlinedInput-input": {
        color: textPrimary,
        padding: "11px 14px",
      },
    },

    /* ── context menu ─────────────────────────────────────── */
    contextMenu: {
      position: "absolute",
      top: 44,
      right: 8,
      background: isDark ? "#1a1c26" : "#fff",
      border: `1px solid ${border}`,
      borderRadius: 10,
      boxShadow: isDark
        ? "0 8px 24px rgba(0,0,0,0.5)"
        : "0 8px 24px rgba(0,0,0,0.12)",
      zIndex: 10,
      overflow: "hidden",
      minWidth: 150,
    },
    contextItem: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "9px 14px",
      fontSize: 12,
      fontWeight: 600,
      color: textSecondary,
      cursor: "pointer",
      transition: "background 0.15s",
      "&:hover": {
        background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
        color: textPrimary,
      },
    },
    contextDanger: {
      color: "#ef4444",
      "&:hover": {
        background: isDark ? "rgba(239, 68, 68, 0.08)" : "rgba(239, 68, 68, 0.05)",
        color: "#dc2626",
      },
    },

    /* ── loading ──────────────────────────────────────────── */
    loadingCenter: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 80,
    },
  };
});

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "agora";
  if (mins < 60) return `${mins}min atras`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h atras`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d atras`;
  return new Date(dateStr).toLocaleDateString("pt-BR");
}

const FlowsPage = () => {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const { user } = useContext(AuthContext);

  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [basesModalOpen, setBasesModalOpen] = useState(false);
  const [triageOpen, setTriageOpen] = useState(false);
  const [agentsOpen, setAgentsOpen] = useState(false);
  const triageEnabled = useTriageEnabled();
  // Com a regra nova ligada, cada botão pela permissão dele; sem ela, só o admin.
  const { can } = usePermission();
  const isAdmin = user?.profile === "admin";
  const canEdit = can("flows.edit", isAdmin);
  const canBases = can("audiences.view", isAdmin);
  const canTriage = triageEnabled && (can("triage.configure", isAdmin) || can("triage.monitor", isAdmin));
  const canAgents = can("ai_agents.view", isAdmin);
  const [newTitle, setNewTitle] = useState("");
  const [newColor, setNewColor] = useState(PALETTE[0]);
  const [creating, setCreating] = useState(false);
  const [menuId, setMenuId] = useState(null);

  const fetchWorkflows = useCallback(async () => {
    try {
      const { data } = await api.get("/workflows");
      setWorkflows(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWorkflows();
  }, [fetchWorkflows]);

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/workflows", {
        title: newTitle.trim(),
        color: newColor,
      });
      setWorkflows((prev) => [data, ...prev]);
      setDialogOpen(false);
      setNewTitle("");
      setNewColor(PALETTE[0]);
      toast.success("Fluxo criado!");
      history.push(`/fluxo/${data.id}`);
    } catch (err) {
      toastError(err);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/workflows/${id}`);
      setWorkflows((prev) => prev.filter((w) => w.id !== id));
      setMenuId(null);
      toast.success("Fluxo removido");
    } catch (err) {
      toastError(err);
    }
  };

  const handleDuplicate = async (wf) => {
    try {
      const { data } = await api.post("/workflows", {
        title: `${wf.title} (copia)`,
        color: wf.color,
      });
      setWorkflows((prev) => [data, ...prev]);
      setMenuId(null);
      toast.success("Fluxo duplicado!");
    } catch (err) {
      toastError(err);
    }
  };

  const handleToggleActive = async (e, wf) => {
    e.stopPropagation();
    try {
      const { data } = await api.put(`/workflows/${wf.id}`, {
        isActive: !wf.isActive,
      });
      setWorkflows((prev) =>
        prev.map((w) => (w.id === data.id ? { ...w, isActive: data.isActive } : w))
      );
    } catch (err) {
      toastError(err);
    }
  };

  const filtered = workflows.filter((w) =>
    w.title.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <MainContainer>
      <div className={classes.page}>
        {/* Header */}
        <div className={classes.header}>
          <div className={classes.headerLeft}>
            <div className={classes.title}>Fluxos</div>
            <div className={classes.subtitle}>
              {workflows.length} fluxo{workflows.length !== 1 ? "s" : ""} configurado{workflows.length !== 1 ? "s" : ""}
            </div>
          </div>
          {(canBases || canTriage || canAgents || canEdit) && (
            <>
              <div style={{ display: "flex", gap: 10 }}>
                {canBases && (
                  <button
                    className={`${classes.actionBtnBase} ${classes.actionBtnSecondary}`}
                    onClick={() => setBasesModalOpen(true)}
                  >
                    <Database size={15} strokeWidth={1.75} />
                    Gerenciar Bases
                  </button>
                )}
                {canAgents && (
                  <button
                    className={`${classes.actionBtnBase} ${classes.actionBtnSecondary}`}
                    onClick={() => setAgentsOpen(true)}
                  >
                    <Bot size={15} strokeWidth={1.75} />
                    {i18n.t("agentManager.button")}
                  </button>
                )}
                {canTriage && (
                  <button
                    className={`${classes.actionBtnBase} ${classes.actionBtnSecondary}`}
                    onClick={() => setTriageOpen(true)}
                  >
                    <ListChecks size={15} strokeWidth={1.75} />
                    {i18n.t("triage.manager.button")}
                  </button>
                )}
                {canEdit && (
                  <button
                    className={`${classes.actionBtnBase} ${classes.actionBtnPrimary}`}
                    onClick={() => setDialogOpen(true)}
                  >
                    <Plus size={15} strokeWidth={1.75} />
                    Novo Fluxo
                  </button>
                )}
              </div>
              {canBases && <ContactBasesModal open={basesModalOpen} onClose={() => setBasesModalOpen(false)} />}
              {canTriage && <TriageManager open={triageOpen} onClose={() => setTriageOpen(false)} />}
              {canAgents && <AgentManager open={agentsOpen} onClose={() => setAgentsOpen(false)} />}
            </>
          )}
        </div>

        {/* Search */}
        {workflows.length > 0 && (
          <div className={classes.searchWrap}>
            <Search size={15} className={classes.searchIcon} />
            <input
              className={classes.searchInput}
              placeholder="Buscar fluxo..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className={classes.loadingCenter}>
            <CircularProgress size={28} style={{ color: "#5B7CFF" }} />
          </div>
        ) : filtered.length === 0 && !search ? (
          <div className={classes.empty}>
            <div className={classes.emptyIcon}>
              <Workflow size={28} color="#5B7CFF" strokeWidth={1.5} />
            </div>
            <div className={classes.emptyTitle}>Nenhum fluxo ainda</div>
            <div className={classes.emptyText}>
              Crie seu primeiro fluxo para automatizar conversas com logica visual, tipo n8n.
            </div>
            {canEdit && (
              <button
                className={`${classes.actionBtnBase} ${classes.actionBtnPrimary}`}
                onClick={() => setDialogOpen(true)}
                style={{ marginTop: 8 }}
              >
                <Plus size={15} strokeWidth={1.75} />
                Criar primeiro fluxo
              </button>
            )}
          </div>
        ) : filtered.length === 0 && search ? (
          <div className={classes.empty}>
            <div className={classes.emptyText}>
              Nenhum fluxo encontrado para "{search}"
            </div>
          </div>
        ) : (
          <div className={classes.grid}>
            {filtered.map((wf) => (
              <div
                key={wf.id}
                className={classes.card}
                onClick={() => {
                  if (menuId === wf.id) return;
                  // Quem vê os resultados (com a regra nova, flows.view; sem ela,
                  // o admin e o super) abre os resultados do fluxo (o editor fica
                  // no botão "Editar fluxo" de lá e no menu do card); os demais
                  // seguem direto para o editor.
                  history.push(can("flows.view", isAdminUser(user)) ? `/fluxo/${wf.id}/resultados` : `/fluxo/${wf.id}`);
                }}
              >
                <div
                  className={classes.cardAccent}
                  style={{ background: wf.color || "#5B7CFF" }}
                />
                <div className={classes.cardBody}>
                  <div className={classes.cardTop}>
                    <div
                      className={classes.cardIcon}
                      style={{
                        background: `${wf.color || "#5B7CFF"}18`,
                      }}
                    >
                      <Workflow
                        size={18}
                        color={wf.color || "#5B7CFF"}
                        strokeWidth={1.8}
                      />
                    </div>
                    {canEdit && (
                      <IconButton
                        size="small"
                        className={classes.cardMenu}
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuId(menuId === wf.id ? null : wf.id);
                        }}
                      >
                        <MoreVertical size={15} />
                      </IconButton>
                    )}
                  </div>

                  <div className={classes.cardTitle}>{wf.title}</div>

                  <div className={classes.cardMeta}>
                    <span className={classes.cardMetaItem}>
                      <Clock size={11} />
                      {timeAgo(wf.updatedAt)}
                    </span>
                  </div>

                  <div className={classes.cardFooter}>
                    <span
                      className={`${classes.cardStatus} ${
                        wf.isActive ? classes.statusActive : classes.statusInactive
                      }`}
                    >
                      <Power size={10} />
                      {wf.isActive ? "Ativo" : "Inativo"}
                    </span>
                    <ChevronRight
                      size={16}
                      className={classes.cardArrow}
                    />
                  </div>
                </div>

                {/* Context menu */}
                {menuId === wf.id && (
                  <div
                    className={classes.contextMenu}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div
                      className={classes.contextItem}
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuId(null);
                        history.push(`/fluxo/${wf.id}`);
                      }}
                    >
                      <Pencil size={13} />
                      Editar fluxo
                    </div>
                    <div
                      className={classes.contextItem}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleActive(e, wf);
                        setMenuId(null);
                      }}
                    >
                      <Power size={13} />
                      {wf.isActive ? "Desativar" : "Ativar"}
                    </div>
                    <div
                      className={classes.contextItem}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDuplicate(wf);
                      }}
                    >
                      <Copy size={13} />
                      Duplicar
                    </div>
                    <div
                      className={`${classes.contextItem} ${classes.contextDanger}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(wf.id);
                      }}
                    >
                      <Trash2 size={13} />
                      Excluir
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Create Dialog ─────────────────────────────────── */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        className={classes.dialog}
      >
        <DialogTitle disableTypography className={classes.dialogTitle}>
          Novo Fluxo
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <label className={classes.fieldLabel}>Titulo</label>
          <TextField
            autoFocus
            fullWidth
            variant="outlined"
            placeholder="Ex: Atendimento principal"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            className={classes.textField}
            onKeyDown={(e) => {
              if (e.key === "Enter" && newTitle.trim()) handleCreate();
            }}
          />

          <label className={classes.fieldLabel} style={{ marginTop: 16 }}>
            Cor
          </label>
          <div className={classes.colorGrid}>
            {PALETTE.map((c) => (
              <div
                key={c}
                className={`${classes.colorDot} ${
                  newColor === c ? classes.colorDotSelected : ""
                }`}
                style={{ background: c }}
                onClick={() => setNewColor(c)}
              />
            ))}
          </div>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button
            className={classes.cancelBtn}
            onClick={() => setDialogOpen(false)}
          >
            Cancelar
          </Button>
          <Button
            className={classes.createBtn}
            variant="contained"
            disabled={!newTitle.trim() || creating}
            onClick={handleCreate}
          >
            {creating ? (
              <CircularProgress size={18} style={{ color: "#fff" }} />
            ) : (
              "Criar"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default FlowsPage;
